import services from 'services';
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import type { ThunkConf } from 'store/thunks';
import axios from 'axios';

export type DialogPreferenceKey = 'dontAskAgainBulkAction' | 'dontAskAgainCompleteReturn' | 'dontAskAgainDeleteReturn' | 'dontAskAgainBulkMaxQty';

type InitialState = {
    userProfile: any | null;
    userProfileLoading: boolean;
    userProfileErrorOnLoading: boolean;
    isAdmin: boolean;
    dialogPreferences: {
        dontAskAgainBulkAction: boolean;
        dontAskAgainCompleteReturn: boolean;
        dontAskAgainDeleteReturn: boolean;
        dontAskAgainBulkMaxQty: boolean;
    };
};

const initialState: InitialState = {
    userProfile: null,
    userProfileLoading: false,
    userProfileErrorOnLoading: false,
    isAdmin: false,
    dialogPreferences: {
        dontAskAgainBulkAction: false,
        dontAskAgainCompleteReturn: false,
        dontAskAgainDeleteReturn: false,
        dontAskAgainBulkMaxQty: false,
    },
};
const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        setIsAdmin: (state, action: PayloadAction<boolean>) => {
            state.isAdmin = action.payload;
        },
        setDialogPreference: (state, action: PayloadAction<{ key: DialogPreferenceKey; value: boolean }>) => {
            state.dialogPreferences[action.payload.key] = action.payload.value;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getUserProfile.pending, (state) => {
            state.userProfileLoading = true;
            state.userProfileErrorOnLoading = false;
        });
        builder.addCase(getUserProfile.rejected, (state) => {
            state.userProfileLoading = false;
            state.userProfileErrorOnLoading = true;
        });
        builder.addCase(getUserProfile.fulfilled, (state, action: PayloadAction<any>) => {
            state.userProfile = action.payload;
            state.userProfileLoading = false;
            state.userProfileErrorOnLoading = false;
        });
        builder.addCase(getDialogPreference.fulfilled, (state, action) => {
            if (action.payload) {
                state.dialogPreferences[action.payload.key] = action.payload.value;
            }
        });
        builder.addCase(getAllDialogPreferences.fulfilled, (state, action) => {
            state.dialogPreferences.dontAskAgainBulkAction =
                action.payload.dontAskAgainBulkAction !== undefined ? action.payload.dontAskAgainBulkAction : false;
            state.dialogPreferences.dontAskAgainCompleteReturn =
                action.payload.dontAskAgainCompleteReturn !== undefined
                    ? action.payload.dontAskAgainCompleteReturn
                    : false;
            state.dialogPreferences.dontAskAgainDeleteReturn =
                action.payload.dontAskAgainDeleteReturn !== undefined ? action.payload.dontAskAgainDeleteReturn : false;
            state.dialogPreferences.dontAskAgainBulkMaxQty =
                action.payload.dontAskAgainBulkMaxQty !== undefined ? action.payload.dontAskAgainBulkMaxQty : false;
        });
        builder.addCase(setDialogPreference.fulfilled, (state, action) => {
            if (action.payload) {
                state.dialogPreferences[action.payload.key] = action.payload.value;
            }
        });
    },
});

const getUserProfile = createAsyncThunk<any, void, ThunkConf>('users/me', async (_, { rejectWithValue }) => {
    try {
        const response = await services.graph.me();
        return response;
    } catch (error) {
        if (axios.isCancel(error)) {
            return rejectWithValue(undefined);
        }
        return rejectWithValue({ message: error instanceof Error ? error.toString() : 'Unknown error' });
    }
});

const getDialogPreference = createAsyncThunk<
    { key: DialogPreferenceKey; value: boolean } | null,
    DialogPreferenceKey,
    ThunkConf
>('users/initializeDialogPreference', async (dialogKey) => {
    try {
        const stored = localStorage.getItem(getStorageKey(dialogKey));
        return { key: dialogKey, value: stored === 'true' };
    } catch (error) {
        console.warn(`Error initializing ${dialogKey} preference:`, error);
        return null;
    }
});

const getAllDialogPreferences = createAsyncThunk<
    { dontAskAgainBulkAction: boolean; dontAskAgainCompleteReturn: boolean; dontAskAgainDeleteReturn: boolean; dontAskAgainBulkMaxQty: boolean },
    void,
    ThunkConf
>('users/initializeAllDialogPreferences', async () => {
    try {
        const dontAskAgainBulkAction = localStorage.getItem(getStorageKey('dontAskAgainBulkAction')) === 'true';
        const dontAskAgainCompleteReturn = localStorage.getItem(getStorageKey('dontAskAgainCompleteReturn')) === 'true';
        const dontAskAgainDeleteReturn = localStorage.getItem(getStorageKey('dontAskAgainDeleteReturn')) === 'true';
        const dontAskAgainBulkMaxQty = localStorage.getItem(getStorageKey('dontAskAgainBulkMaxQty')) === 'true';

        return { dontAskAgainBulkAction, dontAskAgainCompleteReturn, dontAskAgainDeleteReturn, dontAskAgainBulkMaxQty };
    } catch (error) {
        console.warn('Error initializing dialog preferences:', error);
        return { dontAskAgainBulkAction: false, dontAskAgainCompleteReturn: false, dontAskAgainDeleteReturn: false, dontAskAgainBulkMaxQty: false };
    }
});

const setDialogPreference = createAsyncThunk<
    { key: DialogPreferenceKey; value: boolean } | null,
    { key: DialogPreferenceKey; value: boolean },
    ThunkConf
>('users/setDialogPreference', async ({ key, value }) => {
    try {
        localStorage.setItem(getStorageKey(key), value.toString());
        return { key, value };
    } catch (error) {
        console.warn(`Error saving ${key} preference:`, error);
        return null;
    }
});

const selectUserProfile = (state: RootState) => state.users.userProfile;
const selectUserProfileLoading = (state: RootState) => state.users.userProfileLoading;
const selectUserProfileErrorOnLoading = (state: RootState) => state.users.userProfileErrorOnLoading;
const selectIsAdmin = (state: RootState) => state.users.isAdmin;
const selectDialogPreferences = (state: RootState) => state.users.dialogPreferences;

// Helper function to get storage key
const getStorageKey = (dialogKey: DialogPreferenceKey) => `dialogPreference_${dialogKey}`;

export const userSelector = {
    selectUserProfile,
    selectUserProfileLoading,
    selectUserProfileErrorOnLoading,
    selectIsAdmin,
    selectDialogPreferences,
};

export const userThunks = {
    getUserProfile,
    getDialogPreference,
    getAllDialogPreferences,
    setDialogPreference,
};

export const { setIsAdmin } = usersSlice.actions;
export default usersSlice.reducer;
